import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

interface FleetStep {
  step: string;
  total: number;
  assigned: number;
  waiting: number;
  breached: number;
}

interface FleetJobType {
  id: string;
  name: string;
  slaTargetMs: number | null;
  totalDocuments: number;
  slaBreaches: number;
  activeOperators: number;
  steps: FleetStep[];
}

interface FleetData {
  jobTypes: FleetJobType[];
  totalDocuments: number;
  totalOperators: number;
  totalSlaBreaches: number;
}

export function useDashboard() {
  const [fleet, setFleet] = useState<FleetData | null>(null);
  // R-182 — stale-while-revalidate (see useBatches). This hook already avoided
  // flipping `isLoading` true on refresh; the explicit `isRefreshing` flag
  // makes the background-refetch state observable so the page can render a
  // subtle non-blocking cue and gate its skeleton on initial load only.
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsRefreshing(true);
    try {
      setError(null);
      const data = await api.get<FleetData>("/monitor/fleet");
      setFleet(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load fleet data");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();

    const interval = setInterval(() => {
      void load();
    }, 30_000);

    return () => clearInterval(interval);
  }, [load]);

  return { fleet, isLoading, isRefreshing, error, refresh: load };
}
